import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useAuth } from '../../auth/useAuth';
import {
  answerGapQuestion,
  GAP_ANALYSIS_UPDATED_EVENT,
  loadSharedGapQuestionsForPatient,
  type GapDomain,
  type GapSeverity,
  type SharedGapQuestionRecord,
} from '../../utils/patientGapAnalysisStore';

const DOMAIN_META: Record<GapDomain, { label: string; color: string; chipColor: 'error' | 'info' | 'success' | 'default' | 'secondary' }> = {
  CLINICAL:   { label: 'Clinical',   color: '#fdeded', chipColor: 'error' },
  BEHAVIORAL: { label: 'Nutrition',  color: '#f3e5f5', chipColor: 'secondary' },
  CULTURAL:   { label: 'Cultural',   color: '#e3f2fd', chipColor: 'info' },
  FAMILY:     { label: 'Family',     color: '#e8f5e9', chipColor: 'success' },
  SDOH:       { label: 'SDOH',       color: '#f5f5f5', chipColor: 'default' },
};

const SEVERITY_CHIP: Record<GapSeverity, { label: string; color: 'error' | 'warning' | 'success' }> = {
  HIGH:   { label: 'High Priority',   color: 'error' },
  MEDIUM: { label: 'Medium Priority', color: 'warning' },
  LOW:    { label: 'Low Priority',    color: 'success' },
};

export const NutritionistQuestionsPage = () => {
  const { user } = useAuth();
  const [sharedGapQuestions, setSharedGapQuestions] = useState<SharedGapQuestionRecord[]>([]);
  const [responseDrafts, setResponseDrafts] = useState<Record<string, string>>({});

  const resolvedPatientName = user?.displayName ?? 'Sarah Johnson';
  const resolvedPatientId = user?.id;

  useEffect(() => {
    const refresh = () => {
      setSharedGapQuestions(loadSharedGapQuestionsForPatient(resolvedPatientId, resolvedPatientName));
    };
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener(GAP_ANALYSIS_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(GAP_ANALYSIS_UPDATED_EVENT, refresh);
    };
  }, [resolvedPatientId, resolvedPatientName]);

  const handleSubmit = (record: SharedGapQuestionRecord) => {
    const response = responseDrafts[record.question.id]?.trim();
    if (!response) return;
    answerGapQuestion(record.analysisId, record.question.id, response);
    setSharedGapQuestions(loadSharedGapQuestionsForPatient(resolvedPatientId, resolvedPatientName));
    setResponseDrafts((prev) => ({ ...prev, [record.question.id]: '' }));
  };

  const pending = sharedGapQuestions.filter((r) => r.question.patientStatus === 'PENDING_PATIENT');
  const answered = sharedGapQuestions.filter((r) => r.question.patientStatus === 'ANSWERED');

  const renderCard = (record: SharedGapQuestionRecord) => {
    const domain = DOMAIN_META[record.question.domain] ?? DOMAIN_META.SDOH;
    const severity = SEVERITY_CHIP[record.question.severity] ?? SEVERITY_CHIP.MEDIUM;
    const isAnswered = record.question.patientStatus === 'ANSWERED';

    return (
      <Grid item xs={12} key={`${record.analysisId}-${record.question.id}`}>
        <Card
          variant="outlined"
          sx={{
            bgcolor: isAnswered ? 'background.paper' : domain.color,
            borderLeft: 4,
            borderLeftColor: `${severity.color}.main`,
            opacity: isAnswered ? 0.75 : 1,
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
              <Chip label={domain.label} size="small" color={domain.chipColor} />
              <Chip label={severity.label} size="small" color={severity.color} />
              {isAnswered ? (
                <Chip icon={<CheckCircleIcon />} label="Answered" size="small" color="success" variant="outlined" />
              ) : (
                <Chip icon={<HourglassTopIcon />} label="Awaiting your response" size="small" color="warning" variant="outlined" />
              )}
            </Stack>

            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {record.question.question}
            </Typography>
            {record.question.rationale && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {record.question.rationale}
              </Typography>
            )}

            {record.question.patientResponse && (
              <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Your response:</strong> {record.question.patientResponse}
                </Typography>
              </Alert>
            )}

            {!isAnswered && (
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Type your response…"
                  value={responseDrafts[record.question.id] ?? ''}
                  onChange={(e) =>
                    setResponseDrafts((prev) => ({ ...prev, [record.question.id]: e.target.value }))
                  }
                />
                <Button
                  variant="contained"
                  disableElevation
                  disabled={!responseDrafts[record.question.id]?.trim()}
                  onClick={() => handleSubmit(record)}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Submit
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Questions From Your Nutritionist
      </Typography>
      <Typography color="text.secondary" paragraph>
        Review and respond to questions assigned by your care team.
      </Typography>

      {pending.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have <strong>{pending.length}</strong> question{pending.length === 1 ? '' : 's'} waiting for your response.
        </Alert>
      )}

      {sharedGapQuestions.length === 0 ? (
        <Alert severity="success">No questions from your care team right now. Check back later!</Alert>
      ) : (
        <>
          {pending.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>Awaiting Response</Typography>
              <Grid container spacing={2}>
                {pending.map(renderCard)}
              </Grid>
            </Box>
          )}
          {answered.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom color="text.secondary">Answered</Typography>
              <Grid container spacing={2}>
                {answered.map(renderCard)}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
